import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Paper from "apollo-react/components/Paper";
import Select from "apollo-react/components/Select";
import MenuItem from "apollo-react/components/MenuItem";
import Typography from "apollo-react/components/Typography";
import User2 from "apollo-react-icons/User2";
import Email from "apollo-react-icons/Email";
import Calendar from "apollo-react-icons/Calendar";
import Award from "apollo-react-icons/Award";
import CircularProgress from "apollo-react/components/CircularProgress";
import Grid from "apollo-react/components/Grid";
import NewspaperFold from 'apollo-react-icons/NewspaperFold';
import PresentationSpeaker from 'apollo-react-icons/PresentationSpeaker';
import Planner from 'apollo-react-icons/Planner';
import FormSections from "../FormApproveComponents/FormSections/FormSections";
import DrugPricingFormView from "../../Guest/Requests/New_Requests/DrugPricingForm/DrugPricingFormView";
import EctdClarificationWave from "../ECTD/EctdIFrameAttrs";
import { treeData } from "../ECTD/constants";
import styles from "./ClarificationWaves.module.css";
import tabStyles from "./ClarificationWavesTabs.module.css";
import axiosInstance from "../../../axiosInstance";

const getTabsForRole = (role, t, parentStage) => {
  const baseTabs = [
    { label: `${parentStage?.Stage?.Form?.name || t("clarificationWaves.mainForm")}`, value: 0 },
    { label: "eCTD", value: 1 },
    { label: t("clarificationWaves.pricing"), value: 2 }
  ];
  
  return baseTabs;
};

const ClarificationWaves = ({
  subForms,
  requestProgressId,
  persona,
  requestId,
  filledForm,
  requestProgress,
  requestDetails,
  roleName,
  parentStage,
  comments,
  setComments,
}) => {
  const { t } = useTranslation();
  const [selectedWave, setSelectedWave] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [availableWaves, setAvailableWaves] = useState([]);
  const [loadingWaves, setLoadingWaves] = useState(true);
  const [selectedWaveData, setSelectedWaveData] = useState(null);
  const [loadingWaveData, setLoadingWaveData] = useState(false);
  const [filteredEctdComments, setFilteredEctdComments] = useState([]);
  const [ectdComments, setEctdComments] = useState([]);
  const [mainFormComments, setMainFormComments] = useState([]);
  const [pricingComments, setPricingComments] = useState([]);

  useEffect(() => {
    const fetchWaves = async () => {
      if (!requestId) {
        setLoadingWaves(false);
        setAvailableWaves([]);
        return;
      }
      
      setLoadingWaves(true);
      try {
        const endpoint = persona === "customer" 
          ? `/api/clarification-waves/enduser/${requestId}`
          : `/api/clarification-waves/${requestId}`;
        const response = await axiosInstance.get(endpoint);
        if (response.data.success && response.data.waves) {
          setAvailableWaves(response.data.waves);
          
          const urlParams = new URLSearchParams(window.location.search);
          const waveFromUrl = urlParams.get('wave');
          
          if (waveFromUrl && response.data.waves.find(w => w.wave_number === parseInt(waveFromUrl))) {
            setSelectedWave(parseInt(waveFromUrl));
          } else if (response.data.waves.length > 0) {
            const lastWave = response.data.waves[response.data.waves.length - 1];
            setSelectedWave(lastWave.wave_number);
          }
        }
      } catch (error) {
        console.error("Error fetching clarification waves:", error);
      } finally {
        setLoadingWaves(false);
      }
    };

    fetchWaves();
  }, [requestId, persona]);

  useEffect(() => {
    const fetchWaveData = async () => {
      if (!requestId || !selectedWave) return;
      
      setLoadingWaveData(true);
      try {
        const endpoint = persona === "customer"
          ? `/api/clarification-waves/enduser/${requestId}/${selectedWave}`
          : `/api/clarification-waves/${requestId}/${selectedWave}`;
        const response = await axiosInstance.get(endpoint);
        if (response.data.success && response.data.wave) {
          setSelectedWaveData(response.data.wave);
        }
      } catch (error) {
        console.error("Error fetching wave data:", error);
      } finally {
        setLoadingWaveData(false);
      }
    };

    fetchWaveData();
  }, [requestId, selectedWave, persona]);

  useEffect(() => {
    const fetchEctdComments = async () => {
      if (!requestId) {
        setEctdComments([]);
        return;
      }
      
      try {
        const endpoint = persona === "customer"
          ? `/api/ectd-comments/request/enduser/${requestId}`
          : `/api/ectd-comments/request/${requestId}`;
        const response = await axiosInstance.get(endpoint, {
          params: {
            canSeeCommitteeContent: false
          }
        });
        setEctdComments(response.data || []);
      } catch (error) {
        console.error('Error fetching ECTD comments:', error);
        setEctdComments([]);
      }
    };

    fetchEctdComments();
  }, [requestId, persona]);

  useEffect(() => {
    const fetchMainFormComments = async () => {
      if (!requestId) {
        setMainFormComments([]);
        return;
      }
      
      try {
        const endpoint = persona === "customer"
          ? `/api/comments/enduser/${requestId}`
          : `/api/comments/${requestId}`;
        const response = await axiosInstance.get(endpoint, {
          params: { limit: 1000 }
        });
        setMainFormComments(response.data?.data || []);
      } catch (error) {
        console.error('Error fetching main form comments:', error);
        setMainFormComments([]);
      }
    };

    fetchMainFormComments();
  }, [requestId, persona]);

  useEffect(() => {
    const fetchPricingComments = async () => {
      if (!requestId || !selectedWaveData?.drug_pricing_form_snapshot) {
        setPricingComments([]);
        return;
      }
      
      const drugPricingFormId = selectedWaveData.drug_pricing_form_snapshot?.id;
      if (!drugPricingFormId) {
        setPricingComments([]);
        return;
      }
      
      try {
        const endpoint = persona === "customer"
          ? `/api/drug-pricing-form-comments/enduser/${drugPricingFormId}`
          : `/api/drug-pricing-form-comments/${drugPricingFormId}`;
        const response = await axiosInstance.get(endpoint);
        setPricingComments(response.data || []);
      } catch (error) {
        console.error('Error fetching pricing comments:', error);
        setPricingComments([]);
      }
    };

    fetchPricingComments();
  }, [requestId, persona, selectedWaveData?.drug_pricing_form_snapshot]);

  const waveTimeBoundaries = useMemo(() => {
    if (!selectedWave || !availableWaves.length) return null;
    
    const sortedWaves = [...availableWaves].sort((a, b) => a.wave_number - b.wave_number);
    const currentWaveIndex = sortedWaves.findIndex(w => w.wave_number === selectedWave);
    
    if (currentWaveIndex === -1) return null;
    
    const currentWave = sortedWaves[currentWaveIndex];
    const previousWave = currentWaveIndex > 0 ? sortedWaves[currentWaveIndex - 1] : null;
    
    return {
      fromTimestamp: previousWave ? new Date(previousWave.sendback_timestamp) : null,
      toTimestamp: new Date(currentWave.sendback_timestamp)
    };
  }, [selectedWave, availableWaves]);

  const filterCommentsByWaveTime = useCallback((commentsArray) => {
    if (!waveTimeBoundaries || !commentsArray) return [];
    
    return commentsArray.filter(comment => {
      const commentDate = new Date(comment.createdAt);
      const isAfterPrevious = !waveTimeBoundaries.fromTimestamp || commentDate > waveTimeBoundaries.fromTimestamp;
      const isBeforeOrAtCurrent = commentDate <= waveTimeBoundaries.toTimestamp;
      return isAfterPrevious && isBeforeOrAtCurrent;
    });
  }, [waveTimeBoundaries]);

  useEffect(() => {
    if (selectedWaveData?.ectd_file_id && waveTimeBoundaries) {
      const filteredByFile = ectdComments.filter(comment => 
        comment.ectd_file_id === selectedWaveData.ectd_file_id
      );
      const filteredByTime = filterCommentsByWaveTime(filteredByFile);
      setFilteredEctdComments(filteredByTime);
    } else {
      setFilteredEctdComments([]);
    }
  }, [selectedWaveData, ectdComments, waveTimeBoundaries, filterCommentsByWaveTime]);

  const filteredMainFormComments = useMemo(() => {
    return filterCommentsByWaveTime(mainFormComments);
  }, [mainFormComments, filterCommentsByWaveTime]);

  const filteredPricingComments = useMemo(() => {
    return filterCommentsByWaveTime(pricingComments);
  }, [pricingComments, filterCommentsByWaveTime]);

  const handleWaveChange = useCallback((event) => {
    const waveNumber = event.target.value;
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('wave', waveNumber);
    currentUrl.searchParams.set('tab', 'clarifications');
    window.open(currentUrl.toString(), '_blank');
  }, []);

  const handleTabChange = useCallback((value) => {
    setActiveTab(value);
  }, []);

  const getTabIcon = (tabValue) => {
    switch(tabValue) {
      case 0:
        return <NewspaperFold />;
      case 1:
        return <PresentationSpeaker />;
      case 2:
        return <Planner />;
      default:
        return null;
    }
  };

  const waveOptions = useMemo(() => {
    return availableWaves.map(wave => ({
      value: wave.wave_number,
      label: t("clarificationWaves.wave", { number: wave.wave_number })
    }));
  }, [availableWaves, t]);

  const TABS = useMemo(() => 
    getTabsForRole(roleName, t, parentStage),
    [roleName, t, parentStage]
  );

  const parsedFilledFormSnapshot = useMemo(() => {
    if (!selectedWaveData?.filled_form_snapshot) return null;
    try {
      if (typeof selectedWaveData.filled_form_snapshot === 'string') {
        return JSON.parse(selectedWaveData.filled_form_snapshot);
      }
      return selectedWaveData.filled_form_snapshot;
    } catch (e) {
      console.error("Error parsing filled form snapshot:", e);
      return null;
    }
  }, [selectedWaveData]);

  const parsedProductDetailsSnapshot = useMemo(() => {
    if (!selectedWaveData?.product_details_snapshot) return null;
    try {
      if (typeof selectedWaveData.product_details_snapshot === 'string') {
        return JSON.parse(selectedWaveData.product_details_snapshot);
      }
      return selectedWaveData.product_details_snapshot;
    } catch (e) {
      console.error("Error parsing product details snapshot:", e);
      return null;
    }
  }, [selectedWaveData]);

  const parsedManufacturersSnapshot = useMemo(() => {
    if (!selectedWaveData?.manufacturers_site_snapshot) return null;
    try {
      if (typeof selectedWaveData.manufacturers_site_snapshot === 'string') {
        return JSON.parse(selectedWaveData.manufacturers_site_snapshot);
      }
      return selectedWaveData.manufacturers_site_snapshot;
    } catch (e) {
      console.error("Error parsing manufacturers site snapshot:", e);
      return null;
    }
  }, [selectedWaveData]);

  const customSubForms = useMemo(() => {
    const customs = [];
    
    if (parsedProductDetailsSnapshot) {
      customs.push({
        id: 'custom_product_details',
        name: 'Product Details',
        isCustom: true
      });
    }
    
    if (parsedManufacturersSnapshot) {
      customs.push({
        id: 'custom_manufacturers_site',
        name: 'Manufacturers Site',
        isCustom: true
      });
    }
    
    return customs;
  }, [parsedProductDetailsSnapshot, parsedManufacturersSnapshot]);

  const allSubForms = useMemo(() => {
    return [...(subForms || []), ...customSubForms];
  }, [subForms, customSubForms]);

  if (loadingWaves) {
    return (
      <Grid container spacing={2}>
        <Grid item md={12}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: '16px' }}>
              {t("clarificationWaves.loading")}
            </Typography>
          </div>
        </Grid>
      </Grid>
    );
  }

  if (!availableWaves || availableWaves.length === 0) {
    return (
      <Grid container spacing={2}>
        <Grid item md={12}>
          <Paper style={{ padding: '40px', textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              {t("clarificationWaves.noWaves")}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <div className={styles.container}>
      <Paper className={styles.headerPaper}>
        <div className={styles.headerContent}>
          <div className={styles.studiesLabelContainer}>
            <Typography variant="title" emphasis="default">
              {t("clarificationWaves.tabLabel")}
            </Typography>
          </div>
          <div className={styles.waveSelector}>
            <Select
              value={selectedWave}
              onChange={handleWaveChange}
              placeholder={t("clarificationWaves.selectWavePlaceholder")}
              className={styles.select}
              disabled={loadingWaveData}
              maxItems={7}
            >
              {waveOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
        {selectedWaveData && (
          <div style={{ 
            marginTop: '8px', 
            paddingTop: '16px', 
            borderTop: '1px solid #e0e0e0'
          }}>
            <Typography variant="title" emphasis="default" style={{ marginBottom: '16px' }}>
              {t("clarificationWaves.sendBackDetails")}
            </Typography>
            <Grid container  spacing={3}>
              <Grid item xs={12}>
                {selectedWaveData.reviewer ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <User2 style={{ width: '18px', height: '18px', marginTop: '2px', color: '#666' }} />
                        <div>
                          <Typography variant="caption" emphasis="low" style={{ display: 'block', marginBottom: '2px' }}>
                            {t("clarificationWaves.userName")}
                          </Typography>
                          <Typography variant="bodySmall" emphasis="high">
                            {selectedWaveData.reviewer.username}
                          </Typography>
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <Email style={{ width: '18px', height: '18px', marginTop: '2px', color: '#666' }} />
                        <div>
                          <Typography variant="caption" emphasis="low" style={{ display: 'block', marginBottom: '2px' }}>
                            {t("clarificationWaves.userEmail")}
                          </Typography>
                          <Typography variant="bodySmall" emphasis="high">
                            {selectedWaveData.reviewer.email}
                          </Typography>
                        </div>
                      </div>
                    </Grid>
                    {selectedWaveData.reviewer.Role && (
                      <Grid item xs={12} sm={6} md={3}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <Award style={{ width: '18px', height: '18px', marginTop: '2px', color: '#666' }} />
                          <div>
                            <Typography variant="caption" emphasis="low" style={{ display: 'block', marginBottom: '2px' }}>
                              {t("clarificationWaves.userRole")}
                            </Typography>
                            <Typography variant="bodySmall" emphasis="high">
                              {selectedWaveData.reviewer.Role.name}
                            </Typography>
                          </div>
                        </div>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <Calendar style={{ width: '18px', height: '18px', marginTop: '2px', color: '#666' }} />
                        <div>
                          <Typography variant="caption" emphasis="low" style={{ display: 'block', marginBottom: '2px' }}>
                            {t("Date")}
                          </Typography>
                          <Typography variant="bodySmall" emphasis="high">
                            {selectedWaveData.sendback_timestamp ? new Date(selectedWaveData.sendback_timestamp).toLocaleString('en-GB') : 'N/A'}
                          </Typography>
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="bodySmall" color="textSecondary">
                    {selectedWaveData.reviewer_role || 'N/A'}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </div>
        )}
      </Paper>

      <div style={{ display: 'flex', gap: '20px'}}>
        <div className={tabStyles.tabsContainer}>
          {TABS.map((tab, index) => (
            <div
              key={index}
              className={`${tabStyles.tabCard} ${activeTab === index ? tabStyles.active : ''}`}
              onClick={() => handleTabChange(index)}
            >
              <div className={tabStyles.tabIcon}>
                {getTabIcon(tab.value)}
              </div>
              <div className={tabStyles.tabLabel}>
                {tab.label}
              </div>
            </div>
          ))}
        </div>

          {loadingWaveData ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginTop: '16px' }}>
                {t("clarificationWaves.loading")}
              </Typography>
            </div>
          ) : (
            <>
              {TABS[activeTab]?.value === 0 && (
            <div style={{ width: '100%' }}> 
                <FormSections
                  subForms={allSubForms}
                  requestProgressId={requestProgressId}
                  persona={persona}
                  requestId={requestId}
                  filledForm={parsedFilledFormSnapshot ? { value: JSON.stringify(parsedFilledFormSnapshot) } : filledForm}
                  requestProgress={requestProgress}
                  children={null}
                  handleAction={() => {}}
                  roleName={roleName}
                  setComments={setComments}
                  isReadOnly={true}
                  enableAddComment={false}
                  skipCommentsFetch={true}
                  externalComments={filteredMainFormComments}
                  customFormsData={{
                    productDetails: parsedProductDetailsSnapshot,
                    manufacturersSite: parsedManufacturersSnapshot
                  }}
                />
                </div>
              )}

              {TABS[activeTab]?.value === 1 && (
                <EctdClarificationWave
                  ectdVersion={selectedWaveData?.ectd_metadata_snapshot}
                  ectdFileId={selectedWaveData?.ectd_file_id || requestProgress?.request?.ectd_file_id}
                  ectdFile={selectedWaveData?.ectdFile}
                  treeData={treeData}
                  requestProgressId={requestProgressId}
                  requestProgressStatus={requestProgress?.status}
                  comments={filteredEctdComments}
                  setComments={(newComments) => {
                    setEctdComments(newComments);
                    if (setComments) setComments(newComments);
                  }}
                  requestId={requestProgress?.request?.id}
                />
              )}

              {TABS[activeTab]?.value === 2 && selectedWaveData?.drug_pricing_form_snapshot && (
                <DrugPricingFormView
                  drugPricingFormSnapshot={selectedWaveData.drug_pricing_form_snapshot}
                  persona={persona}
                  role={roleName}
                  requestProgressStatus={requestProgress?.status}
                  comments={filteredPricingComments}
                  setComments={setComments}
                  enableAddComment={false}
                />
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default ClarificationWaves;

